import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { EmptyStateView } from '../../components/EmptyStateView';
import { SAM_LANGUAGE_CODE } from '../../domain/user';
import { useCompanyQuery } from '../../features/company/useCompanyQuery';
import { useCompetitorsQuery } from '../../features/competitors/useCompetitorsQuery';
import { useLinkedinContactQuery } from '../../features/contacts/linkedinContact/useLinkedinContactQuery';
import { useMetricsLabelsQuery } from '../../features/preferences/useMetricsLabelsQuery';
import { useValuePropPreferencesQuery } from '../../features/preferences/useValuePropPreferencesQuery';
import { useUserSettings } from '../../features/user/useUserSettings';
import { loadSAMTranslations } from '../../services/i18n';
import { StyledContainerWithNav } from '../styles';

import { EmailPreview } from './EmailPreview';
import { StyledEmailsContainer } from './EmailPreview/styles';
import { InsightsHeading } from './InsightsHeading';
import { InsightsMigrationBanner } from './InsightsMigrationBanner';
import { LoadingView } from './LoadingView';
import { LoadMoreButton } from './LoadMoreButton';
import { Preferences } from './Preferences';
import { StyledInsightsSubHeading, StyledInsightsViewContainer } from './styles';
import { useGenerateEmailsList } from './useGenerateEmailsList';

const initialEmailsCount = 3;

export const InsightsView = () => {
  const { t } = useTranslation();
  const { getUserSetting } = useUserSettings();
  const [, forceUpdate] = useState(0);

  const languageCode = getUserSetting(SAM_LANGUAGE_CODE);

  useEffect(() => {
    const loadTranslations = async () => {
      await loadSAMTranslations(languageCode || 'en');
      forceUpdate((n) => n + 1);
    };

    void loadTranslations();
  }, [languageCode]);

  const {
    emailsToGenerate,
    queriesByInsight,
    generateMoreEmail,
    regenerateEmail,
    isInitialListLoading,
    isLoading,
    isEmptyState,
  } = useGenerateEmailsList();

  useLinkedinContactQuery();
  useCompetitorsQuery();
  useMetricsLabelsQuery();
  useValuePropPreferencesQuery();
  const companyQuery = useCompanyQuery();

  const isInitialLoading = companyQuery.isLoading || isInitialListLoading;

  if (isInitialLoading) {
    return <LoadingView variant="list" />;
  }

  if (!companyQuery.isLoading && !companyQuery.data) {
    return (
      <StyledContainerWithNav data-automation="CompanyEmptyState">
        <EmptyStateView
          messageKey="company.empty_state.message_linkedin"
          img="/images/empty-state-lens.svg"
          variant="lens"
        />
      </StyledContainerWithNav>
    );
  }

  return (
    <StyledInsightsViewContainer data-automation="InsightsView">
      <InsightsMigrationBanner />
      <InsightsHeading />
      <StyledInsightsSubHeading>{t('insights.subheading')}</StyledInsightsSubHeading>
      <Preferences />
      {isEmptyState ? (
        <EmptyStateView
          textKey="insights.empty_state.text"
          messageKey="insights.empty_state.message"
          img="/images/empty-state-cactus.svg"
          variant="cactus"
        />
      ) : (
        <StyledEmailsContainer data-automation="InsightsEmails">
          {queriesByInsight.map(({ insight, queries }, index) => {
            const position = `${index + 1} out of ${emailsToGenerate}`;
            return (
              <EmailPreview
                key={insight.id}
                insight={insight}
                queries={queries}
                onRegenerate={regenerateEmail}
                positionInList={position}
                isFirstBatch={index < initialEmailsCount}
              />
            );
          })}

          <LoadMoreButton
            generateMoreEmail={generateMoreEmail}
            isLoading={isLoading}
            emailsToGenerate={emailsToGenerate}
          />
        </StyledEmailsContainer>
      )}
    </StyledInsightsViewContainer>
  );
};
